import { useState } from 'react';
import { X, AlertTriangle, Shield, Bug, FileText, MessageSquare } from 'lucide-react';

/**
 * RejectionModal - Modal for admins to reject a workflow with structured feedback
 * Replaces the browser prompt() with a proper UI
 */
const RejectionModal = ({ workflow, isOpen, onClose, onReject }) => {
  const [feedbackType, setFeedbackType] = useState('security_issue');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const feedbackTypes = [
    {
      id: 'security_issue',
      label: 'Security Issue',
      description: 'Code contains security vulnerabilities (command injection, XSS, etc.)',
      icon: Shield,
      color: 'text-red-400'
    },
    {
      id: 'malware_concern',
      label: 'Malware Concern',
      description: 'Potentially malicious code detected (data theft, backdoors, etc.)',
      icon: AlertTriangle,
      color: 'text-red-500'
    },
    {
      id: 'quality_issue',
      label: 'Quality Issue',
      description: 'Code quality or functionality problems',
      icon: Bug,
      color: 'text-yellow-400'
    },
    {
      id: 'metadata_issue',
      label: 'Metadata Issue',
      description: 'Missing or incorrect description, tags, or screenshots',
      icon: FileText,
      color: 'text-blue-400'
    },
    {
      id: 'general',
      label: 'General',
      description: 'Other feedback not covered above',
      icon: MessageSquare,
      color: 'text-gray-400'
    }
  ];

  const handleSubmit = async () => {
    if (!message.trim()) {
      alert('Please provide feedback explaining what needs to be fixed.');
      return;
    }

    setSubmitting(true);
    try {
      await onReject(workflow.id, feedbackType, message.trim());
      onClose();
    } catch (error) {
      alert('Failed to reject workflow: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFeedbackType('security_issue');
      setMessage('');
      onClose();
    }
  };

  if (!isOpen || !workflow) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-gray-900 rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-white">Reject Workflow</h2>
            <p className="text-sm text-gray-400 truncate">{workflow.title}</p>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Feedback Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Issue Type
            </label>
            <div className="space-y-2">
              {feedbackTypes.map((type) => {
                const Icon = type.icon;
                const isSelected = feedbackType === type.id;

                return (
                  <button
                    key={type.id}
                    onClick={() => setFeedbackType(type.id)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                  >
                    <Icon className={`h-5 w-5 mt-0.5 ${type.color}`} />
                    <div>
                      <div className="font-medium text-white">{type.label}</div>
                      <div className="text-sm text-gray-400">{type.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Detailed Feedback <span className="text-red-400">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Explain what needs to be fixed so the author can address the issue..."
              rows={5}
              className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Be specific about what&apos;s wrong and how to fix it.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-800">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !message.trim()}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Rejecting...
              </>
            ) : (
              'Reject with Feedback'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectionModal;
